import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import type {
  ProjectTemplateRecipientType,
  SenderStakeholderType,
} from '../../config/templateConfig'
import { type Attachment, PaymentType, type Project } from '../../types/project'
import type { Contact, User } from '../../types/user'
import {
  type VisibilityContext,
  getProjectFieldVisibility,
} from '../../utils/fieldVisibility'
import ContactInfoDisplay from '../ContactInfoDisplay'
import LiveDateTime from '../LiveDateTime'
import OpenMap from '../OpenMaps'

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

// Helper to get file icon based on content type
const getFileIcon = (contentType: string): string => {
  if (contentType.startsWith('image/')) return 'fa-file-image'
  if (contentType === 'application/pdf') return 'fa-file-pdf'
  if (contentType.includes('word') || contentType.includes('document'))
    return 'fa-file-word'
  if (contentType.includes('excel') || contentType.includes('spreadsheet'))
    return 'fa-file-excel'
  if (contentType.includes('text')) return 'fa-file-lines'
  if (contentType.includes('zip') || contentType.includes('compressed'))
    return 'fa-file-zipper'
  return 'fa-file'
}

// Pending file type for files not yet uploaded
export interface PendingFile {
  name: string
  size: number
  type: string
}

interface ProjectConfirmationProps {
  data: Partial<Project>
  contacts: Contact[]
  creator?: User | null
  useDirectData?: boolean // When true, use data directly without API calls
  visibilityContext?: VisibilityContext // Optional visibility context for conditional field hiding
  templateRecipientType?: ProjectTemplateRecipientType | null // Template type for conditional field display
  pendingAttachments?: PendingFile[] // Pending files not yet uploaded
  showLiveSentDate?: boolean // When true, show live updating "Sent Date" field
  showAcceptedDate?: boolean // When true, show accepted date for accepted projects
  parentProject?: Project | null // Parent project for sub-contract flow
  senderStakeholderType?: SenderStakeholderType | null // Sender type for correct label display
}

const ProjectConfirmation: React.FC<ProjectConfirmationProps> = ({
  data,
  contacts,
  creator,
  useDirectData = false,
  visibilityContext,
  templateRecipientType,
  pendingAttachments = [],
  showLiveSentDate = false,
  showAcceptedDate = false,
  parentProject = null,
  senderStakeholderType = null,
}) => {
  // Determine the actual sender stakeholder type from props or data
  const actualSenderType: SenderStakeholderType =
    senderStakeholderType ||
    (data.sender_stakeholder_type as SenderStakeholderType) ||
    'CONTRACTOR'

  // Get the label for the sender based on their stakeholder type
  const getSenderLabel = (): string => {
    switch (actualSenderType) {
      case 'SUB_HAULER':
        return 'Sub Hauler'
      case 'BROKER':
        return 'Broker'
      default:
        return 'Contractor'
    }
  }

  // Check if this is a sub-contract (has parent project)
  const isSubContract = !!(data.parent_project_id || parentProject)
  // Get field visibility based on sender/recipient relationship
  const fieldVisibility = visibilityContext
    ? getProjectFieldVisibility(visibilityContext)
    : { showContractorFields: true, showThirdPartyFields: true, isSender: true }
  const [siteSupervisorDisplayNames, setSiteSupervisorDisplayNames] = useState<
    string[]
  >([])
  const [isLoadingNames, setIsLoadingNames] = useState<boolean>(true)

  // Get display names from available data
  useEffect(() => {
    setIsLoadingNames(true)

    // Helper function to format user name with company
    const formatUserName = (user: User) => {
      const fullName = `${user.first_name} ${user.last_name}`.trim()
      const companyName = user.company?.name
      return companyName ? `${fullName} (${companyName})` : fullName
    }

    // Set site supervisor names
    if (data.site_supervisors && data.site_supervisors.length > 0) {
      const siteSupervisorNames = data.site_supervisors.map(formatUserName)
      setSiteSupervisorDisplayNames(siteSupervisorNames)
    } else {
      setSiteSupervisorDisplayNames([])
    }

    setIsLoadingNames(false)
  }, [data.site_supervisors])

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US')
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const dateStr = date.toLocaleDateString('en-US') // MM/DD/YYYY format
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) // HH:MM AM/PM format
    return `${dateStr} at ${timeStr}`
  }

  // Helper function to format contact info with email and phone
  const formatContactInfo = (user: User) => {
    const fullName = `${user.first_name} ${user.last_name}`.trim()
    const parts: string[] = []

    if (fullName) {
      parts.push(fullName)
    }

    if (user.email) {
      parts.push(`(${user.email})`)
    }

    if (user.phone_number) {
      parts.push(`- ${user.phone_number}`)
    }

    return parts.join(' ') || '-'
  }

  const toTitleCase = (str?: string) => {
    if (!str) return '-'
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  // Site supervisor display
  const displaySiteSupervisors = () => {
    // First priority: Check for template form fields (individual fields)
    if (data.site_supervisor_first_name || data.site_supervisor_last_name) {
      const fullName =
        `${data.site_supervisor_first_name || ''} ${data.site_supervisor_last_name || ''}`.trim()
      const contactInfo: string[] = []

      if (data.site_supervisor_email) {
        contactInfo.push(data.site_supervisor_email)
      }

      if (data.site_supervisor_phone) {
        contactInfo.push(data.site_supervisor_phone)
      }

      if (contactInfo.length > 0) {
        return (
          <>
            {fullName}
            <br />
            {contactInfo.join(' | ')}
          </>
        )
      }

      return fullName || '-'
    }

    // Second priority: Use the stored site_supervisor_users data from form selection
    if (data.site_supervisor_users && data.site_supervisor_users.length > 0) {
      return data.site_supervisor_users
        .map((user) => formatContactInfo(user))
        .join(', ')
    }

    // If we have site_supervisors from API, use them with contact info
    if (data.site_supervisors && data.site_supervisors.length > 0) {
      return data.site_supervisors
        .map((user) => formatContactInfo(user))
        .join(', ')
    }

    if (siteSupervisorDisplayNames.length > 0) {
      return siteSupervisorDisplayNames.join(', ')
    }
    return '-'
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>PROJECT DETAILS</h3>

      {/* Parent Project Reference - shown for sub-contract flow */}
      {parentProject && (
        <div className={css(styles.section, styles.parentProjectSection)}>
          <h4 className={css(styles.subTitle)}>Parent Project Reference</h4>
          <div className={css(styles.grid)}>
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Parent Project</span>
              <span className={css(styles.value)}>{parentProject.label}</span>
            </div>
            {/* Original Contractor from parent project */}
            {parentProject.contractors &&
              parentProject.contractors.length > 0 && (
                <div className={css(styles.field)}>
                  <span className={css(styles.label)}>Original Contractor</span>
                  <span className={css(styles.value)}>
                    {parentProject.contractors[0].first_name}{' '}
                    {parentProject.contractors[0].last_name}
                    {parentProject.contractors[0].company?.name && (
                      <> ({parentProject.contractors[0].company.name})</>
                    )}
                  </span>
                </div>
              )}
            {/* Original Sub Hauler from parent project (the person who is now creating the sub-contract) */}
            {parentProject.sub_haulers &&
              parentProject.sub_haulers.length > 0 && (
                <div className={css(styles.field)}>
                  <span className={css(styles.label)}>Original Sub Hauler</span>
                  <span className={css(styles.value)}>
                    {parentProject.sub_haulers[0].first_name}{' '}
                    {parentProject.sub_haulers[0].last_name}
                    {parentProject.sub_haulers[0].company?.name && (
                      <> ({parentProject.sub_haulers[0].company.name})</>
                    )}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Original Project Parties - Loading placeholder for sub-contracts while data loads */}
      {!parentProject &&
        isSubContract &&
        !(
          data.parent_project_parties?.contractors?.length ||
          data.parent_project_parties?.brokers?.length ||
          data.parent_project_parties?.site_supervisors?.length ||
          data.parent_project_parties?.sub_haulers?.length
        ) && (
          <div className={css(styles.section, styles.parentProjectSection)}>
            <h4 className={css(styles.subTitle)}>Original Project Parties</h4>
            <div className={css(styles.loadingPlaceholder)}>
              <div className={css(styles.loadingRow)}>
                <div className={css(styles.loadingLabel)} />
                <div className={css(styles.loadingValue)} />
              </div>
              <div className={css(styles.loadingRow)}>
                <div className={css(styles.loadingLabel)} />
                <div className={css(styles.loadingValue)} />
              </div>
            </div>
          </div>
        )}

      {/* Original Project Parties - shown for sub-contracts when viewing (no parentProject prop but has parent_project_parties) */}
      {!parentProject &&
        isSubContract &&
        !!(
          data.parent_project_parties?.contractors?.length ||
          data.parent_project_parties?.brokers?.length ||
          data.parent_project_parties?.site_supervisors?.length ||
          data.parent_project_parties?.sub_haulers?.length
        ) && (
          <div className={css(styles.section, styles.parentProjectSection)}>
            <h4 className={css(styles.subTitle)}>Original Project Parties</h4>
            <div className={css(styles.grid)}>
              {data.parent_project_label && (
                <div className={css(styles.field)}>
                  <span className={css(styles.label)}>Parent Project</span>
                  <span className={css(styles.value)}>
                    {data.parent_project_label}
                  </span>
                </div>
              )}
              {/* Original Contractor from parent */}
              {data.parent_project_parties.contractors &&
                data.parent_project_parties.contractors.length > 0 && (
                  <div className={css(styles.field)}>
                    <span className={css(styles.label)}>
                      Original Contractor
                    </span>
                    <span className={css(styles.value)}>
                      {data.parent_project_parties.contractors[0].first_name}{' '}
                      {data.parent_project_parties.contractors[0].last_name}
                      {data.parent_project_parties.contractors[0]
                        .company_name && (
                        <>
                          {' '}
                          (
                          {
                            data.parent_project_parties.contractors[0]
                              .company_name
                          }
                          )
                        </>
                      )}
                    </span>
                  </div>
                )}
              {/* Original Broker from parent */}
              {data.parent_project_parties.brokers &&
                data.parent_project_parties.brokers.length > 0 && (
                  <div className={css(styles.field)}>
                    <span className={css(styles.label)}>Original Broker</span>
                    <span className={css(styles.value)}>
                      {data.parent_project_parties.brokers[0].first_name}{' '}
                      {data.parent_project_parties.brokers[0].last_name}
                      {data.parent_project_parties.brokers[0].company_name && (
                        <>
                          {' '}
                          ({data.parent_project_parties.brokers[0].company_name}
                          )
                        </>
                      )}
                    </span>
                  </div>
                )}
              {/* Original Site Supervisor from parent */}
              {data.parent_project_parties.site_supervisors &&
                data.parent_project_parties.site_supervisors.length > 0 && (
                  <div className={css(styles.field)}>
                    <span className={css(styles.label)}>
                      Original Site Supervisor
                    </span>
                    <span className={css(styles.value)}>
                      {
                        data.parent_project_parties.site_supervisors[0]
                          .first_name
                      }{' '}
                      {
                        data.parent_project_parties.site_supervisors[0]
                          .last_name
                      }
                    </span>
                  </div>
                )}
              {/* Original Sub Hauler from parent */}
              {data.parent_project_parties.sub_haulers &&
                data.parent_project_parties.sub_haulers.length > 0 && (
                  <div className={css(styles.field)}>
                    <span className={css(styles.label)}>
                      Original Sub Hauler
                    </span>
                    <span className={css(styles.value)}>
                      {data.parent_project_parties.sub_haulers[0].first_name}{' '}
                      {data.parent_project_parties.sub_haulers[0].last_name}
                      {data.parent_project_parties.sub_haulers[0]
                        .company_name && (
                        <>
                          {' '}
                          (
                          {
                            data.parent_project_parties.sub_haulers[0]
                              .company_name
                          }
                          )
                        </>
                      )}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>
          Parties and their Contact Information
        </h4>
        <div className={css(styles.grid)}>
          {/* Sender field - label changes based on sender stakeholder type */}
          {/* For sub-contracts from sub-haulers: shows "Sub Hauler" (the sender/creator) */}
          {/* For regular projects from contractors: shows "Contractor" */}
          {fieldVisibility.showContractorFields && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>{getSenderLabel()}</span>
              {isLoadingNames ? (
                <span className={css(styles.value)}>Loading...</span>
              ) : (
                <ContactInfoDisplay
                  user={
                    // Priority: 1) snapshot fields, 2) contractors array (for contractors), 3) creator prop (for creation flow)
                    data.created_by_first_name || data.created_by_last_name
                      ? {
                          first_name: data.created_by_first_name || '',
                          last_name: data.created_by_last_name || '',
                          email: data.created_by_email || '',
                          company: data.created_by_company_name
                            ? {
                                name: data.created_by_company_name,
                                address: data.created_by_company_address,
                              }
                            : undefined,
                        }
                      : actualSenderType === 'CONTRACTOR'
                        ? data.contractors?.[0] || creator
                        : creator
                  }
                  variant="inline"
                  showCompany
                />
              )}
            </div>
          )}
          {/* Project Manager field - shown when project managers are assigned */}
          {data.project_managers && data.project_managers.length > 0 && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Project Manager</span>
              <ContactInfoDisplay
                user={data.project_managers[0]}
                variant="inline"
                showCompany
              />
            </div>
          )}
          {/* Administrator field - shown when administrators are assigned */}
          {data.administrators && data.administrators.length > 0 && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Administrator</span>
              <ContactInfoDisplay
                user={data.administrators[0]}
                variant="inline"
                showCompany
              />
            </div>
          )}
          {/* Broker field - shown when brokers are assigned, regardless of sender/recipient role */}
          {(data.brokers?.length || data.broker_users?.length) &&
            (!templateRecipientType || templateRecipientType === 'BROKER') && (
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Broker</span>
                <ContactInfoDisplay
                  user={data.brokers?.[0] || data.broker_users?.[0]}
                  variant="inline"
                  showCompany
                />
              </div>
            )}
          {/* Third-Party Broker field - shown when third-party brokers are assigned */}
          {data.third_party_brokers?.length ? (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Third-Party Broker</span>
              <ContactInfoDisplay
                user={data.third_party_brokers[0]}
                variant="inline"
                showCompany
              />
            </div>
          ) : null}
          {/* Subhauler field - only shown for SUB_HAULER template type */}
          {fieldVisibility.showThirdPartyFields &&
            templateRecipientType === 'SUB_HAULER' && (
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Sub Hauler</span>
                <ContactInfoDisplay
                  user={data.sub_haulers?.[0] || data.sub_hauler_users?.[0]}
                  variant="inline"
                  showCompany
                />
              </div>
            )}
          {/* Third Party Contractor field - only shown for THIRD_PARTY_CONTRACTOR template type */}
          {fieldVisibility.showThirdPartyFields &&
            templateRecipientType === 'THIRD_PARTY_CONTRACTOR' && (
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>
                  Third Party Contractor
                </span>
                <ContactInfoDisplay
                  user={data.third_party_contractors?.[0]}
                  variant="inline"
                  showCompany
                />
              </div>
            )}
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Site Supervisor</span>
            <span className={css(styles.value)}>
              {displaySiteSupervisors()}
            </span>
          </div>
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Job Details</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Project ID</span>
            <span className={css(styles.value)}>{data.label || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Creation Date</span>
            <span className={css(styles.value)}>
              {data.created_date_formatted || formatDateTime(data.created_date)}
            </span>
          </div>
          {/* Show live sent date when in confirmation step, or static date if already sent */}
          {showLiveSentDate ? (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Sent Date</span>
              <LiveDateTime className={css(styles.value)} />
            </div>
          ) : data.sent_at_formatted ? (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Sent Date</span>
              <span className={css(styles.value)}>
                {data.sent_at_formatted}
              </span>
            </div>
          ) : null}
          {/* Show accepted date for accepted projects */}
          {showAcceptedDate && data.accepted_at && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Accepted Date</span>
              <span className={css(styles.value)}>
                {formatDateTime(data.accepted_at)}
              </span>
            </div>
          )}
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Job Site</span>
            <span className={css(styles.value)}>{data.job_site || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Job ID</span>
            <span className={css(styles.value)}>{data.job_id || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>PO Number</span>
            <span className={css(styles.value)}>{data.po_number || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Starting Date</span>
            <span className={css(styles.value)}>
              {formatDateTime(data.starting_date)}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Completion Date</span>
            <span className={css(styles.value)}>
              {formatDate(data.completion_date)}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Pick Up Location</span>
            <span className={css(styles.value)}>
              {data.pickup_address ? (
                <OpenMap address={data.pickup_address} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Drop Off Location</span>
            <span className={css(styles.value)}>
              {data.dropoff_address ? (
                <OpenMap address={data.dropoff_address} />
              ) : (
                '-'
              )}
            </span>
          </div>
          {data.third_party_location && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Third Party Location</span>
              <span className={css(styles.value)}>
                <OpenMap address={data.third_party_location} />
              </span>
            </div>
          )}
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Material(s)</span>
            <span className={css(styles.value)}>{data.material || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Number of Trucks</span>
            <span className={css(styles.value)}>
              {data.trucks_number || '-'}
            </span>
          </div>
          {data.truck_types && data.truck_types.length > 0 && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Truck Types</span>
              <span className={css(styles.value)}>
                {data.truck_types.join(', ')}
              </span>
            </div>
          )}
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Invoices</span>
            <span className={css(styles.value)}>
              {data.invoice_interval
                ? data.invoice_interval.charAt(0).toUpperCase() +
                  data.invoice_interval.slice(1)
                : '-'}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Status Reports</span>
            <span className={css(styles.value)}>
              {data.status_interval
                ? data.status_interval.charAt(0).toUpperCase() +
                  data.status_interval.slice(1)
                : '-'}
            </span>
          </div>
          {data.notes && (
            <div className={css(styles.field, styles.fullWidth)}>
              <span className={css(styles.label)}>Notes</span>
              <span className={css(styles.value, styles.notesValue)}>
                {data.notes}
              </span>
            </div>
          )}
          <div className={css(styles.field, styles.fullWidth)}>
            <span className={css(styles.label)}>Attachments</span>
            {(data.attachments && data.attachments.length > 0) ||
            pendingAttachments.length > 0 ? (
              <div className={css(styles.attachmentList)}>
                {/* Existing attachments */}
                {data.attachments?.map((attachment: Attachment) => (
                  <div
                    key={attachment.id}
                    className={css(styles.attachmentItem)}
                  >
                    <i
                      className={`fa-regular ${getFileIcon(attachment.content_type_str)} ${css(styles.attachmentIcon)}`}
                    />
                    <a
                      href={attachment.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css(styles.attachmentLink)}
                      title={attachment.original_filename}
                    >
                      {attachment.original_filename}
                    </a>
                    <span className={css(styles.attachmentSize)}>
                      ({formatFileSize(attachment.file_size)})
                    </span>
                  </div>
                ))}
                {/* Pending files (not yet uploaded) */}
                {pendingAttachments.map((file, index) => (
                  <div
                    key={`pending-${file.name}-${file.size}-${index}`}
                    className={css(styles.attachmentItem)}
                  >
                    <i
                      className={`fa-regular ${getFileIcon(file.type)} ${css(styles.attachmentIcon)}`}
                    />
                    <span
                      className={css(styles.attachmentName)}
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span className={css(styles.attachmentSize)}>
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className={css(styles.value)}>No attachments</span>
            )}
          </div>
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Payment Information</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Payment Type</span>
            <span className={css(styles.value)}>
              {data.payment_type === PaymentType.MILESTONE
                ? 'Milestone Payment'
                : data.payment_type === PaymentType.PROGRESS
                  ? 'Progress Payment'
                  : 'Rate Type'}
            </span>
          </div>
          {(!data.payment_type ||
            data.payment_type === PaymentType.RATE_TYPE) && (
            <>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Rate Type</span>
                <span className={css(styles.value)}>
                  {toTitleCase(data.rate_type)}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Pay Rate</span>
                <span className={css(styles.value)}>
                  {data.pay_rate ? `$${data.pay_rate}` : '-'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Payment Schedule Display for Milestone/Progress */}
        {data.payment_type &&
          data.payment_type !== PaymentType.RATE_TYPE &&
          data.payment_schedule &&
          data.payment_schedule.length > 0 && (
            <div className={css(styles.scheduleSection)}>
              <h5 className={css(styles.scheduleTitle)}>
                {data.payment_type === PaymentType.MILESTONE
                  ? 'Milestone Schedule'
                  : 'Progress Payment Schedule'}
              </h5>
              <div className={css(styles.scheduleTable)}>
                <div className={css(styles.scheduleHeader)}>
                  <span
                    className={css(
                      styles.scheduleHeaderCell,
                      styles.deliverableCol,
                    )}
                  >
                    Deliverable
                  </span>
                  <span
                    className={css(styles.scheduleHeaderCell, styles.dateCol)}
                  >
                    Due Date
                  </span>
                  <span
                    className={css(styles.scheduleHeaderCell, styles.amountCol)}
                  >
                    Pay
                  </span>
                  <span
                    className={css(styles.scheduleHeaderCell, styles.amountCol)}
                  >
                    Fee
                  </span>
                </div>
                {data.payment_schedule.map((entry, index) => (
                  <div
                    key={`schedule-${entry.deliverable}-${entry.due_date}-${index}`}
                    className={css(styles.scheduleRow)}
                  >
                    <span
                      className={css(
                        styles.scheduleCell,
                        styles.deliverableCol,
                      )}
                    >
                      {entry.deliverable || '-'}
                    </span>
                    <span className={css(styles.scheduleCell, styles.dateCol)}>
                      {entry.due_date
                        ? new Date(entry.due_date).toLocaleDateString('en-US')
                        : '-'}
                    </span>
                    <span
                      className={css(styles.scheduleCell, styles.amountCol)}
                    >
                      ${Number(entry.pay_amount || 0).toFixed(2)}
                    </span>
                    <span
                      className={css(styles.scheduleCell, styles.amountCol)}
                    >
                      ${Number(entry.fee_amount || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className={css(styles.scheduleTotals)}>
                  <span className={css(styles.totalLabel)}>
                    Total Scheduled Payment:
                  </span>
                  <span className={css(styles.totalAmount)}>
                    ${Number(data.total_scheduled_payment || 0).toFixed(2)}
                  </span>
                </div>
                <div className={css(styles.scheduleTotals)}>
                  <span className={css(styles.totalLabel)}>
                    Total Scheduled Fees:
                  </span>
                  <span className={css(styles.totalAmount)}>
                    ${Number(data.total_scheduled_fees || 0).toFixed(2)}
                  </span>
                </div>
                <div
                  className={css(styles.scheduleTotals, styles.grandTotalRow)}
                >
                  <span className={css(styles.totalLabel)}>Grand Total:</span>
                  <span className={css(styles.totalAmount)}>
                    $
                    {(
                      Number(data.total_scheduled_payment || 0) +
                      Number(data.total_scheduled_fees || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  section: {
    marginBottom: '24px',
  },
  parentProjectSection: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #E5E7EB',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '24px',
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#6B7280',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: '1.4',
  },
  value: {
    color: '#111827',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  notesValue: {
    whiteSpace: 'pre-wrap',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #E5E7EB',
    margin: '24px 0',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  scheduleSection: {
    marginTop: '20px',
  },
  scheduleTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '12px',
  },
  scheduleTable: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #E5E7EB',
  },
  scheduleHeader: {
    display: 'flex',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '8px',
    marginBottom: '8px',
  },
  scheduleHeaderCell: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  scheduleRow: {
    display: 'flex',
    padding: '8px 0',
    borderBottom: '1px solid #E5E7EB',
  },
  scheduleCell: {
    fontSize: '13px',
    color: '#111827',
  },
  deliverableCol: {
    flex: 2,
    paddingRight: '12px',
  },
  dateCol: {
    flex: 1,
    paddingRight: '12px',
  },
  amountCol: {
    flex: 1,
    textAlign: 'right' as const,
  },
  scheduleTotals: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #E5E7EB',
  },
  totalLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  totalAmount: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
  },
  grandTotalRow: {
    borderBottom: 'none',
    paddingTop: '12px',
    marginTop: '4px',
    borderTop: '2px solid #374151',
  },
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    gap: '8px',
  },
  attachmentIcon: {
    fontSize: '16px',
    color: '#6B7280',
    flexShrink: 0,
  },
  attachmentLink: {
    fontSize: '13px',
    color: '#374151',
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': {
      color: '#1C64F2',
      textDecoration: 'underline',
    },
  },
  attachmentName: {
    fontSize: '13px',
    color: '#374151',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attachmentSize: {
    fontSize: '12px',
    color: '#9CA3AF',
    flexShrink: 0,
  },
  loadingPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  loadingRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  loadingLabel: {
    height: '12px',
    width: '80px',
    backgroundColor: '#D1D5DB',
    borderRadius: '4px',
  },
  loadingValue: {
    height: '16px',
    width: '160px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
  },
})

export default ProjectConfirmation
